import { __makeTemplateObject, __assign } from '../../node_modules/tslib/tslib.es6.js';
import React, { useRef } from 'react';
import './index.css.js';
import { isEnterOrSpacePressed } from '../../utils/functions.js';
import styled from 'styled-components';
import { themeGet } from '@primer/react';
import LTSelect from '../LTSelect/index.js';

/**
 * LTSegmentedControl component.
 *@param {!Array<!Object<string, string>} options - Dropdown options in the menu.
    Each key will have the following
   - label: string
   - value: string
   - icon: string
 * @param {string} selectedValue - The currently selected value.
 * @param {Function} onChange - Function to handle value change.
 * @param {string} [ariaLabel=''] - Aria label for accessibility.
 * @param {!Object<string, *>} [imageStyles={}] - Styles for the image.
 * @param {!Object<string, *>} [buttonStyles={}] - Styles for the button.
 * @param {bool} [isIconButton=false] - Whether the button is an icon button.
 * @param {string} [className=''] - CSS class for the outer element.
 * @returns {JSX.Element} LTSegmentedControl component.
 */
var LTSegmentedControl = function LTSegmentedControl(_a) {
  var _b = _a.ariaLabel,
    ariaLabel = _b === void 0 ? "" : _b,
    _c = _a.ariaLabelledBy,
    ariaLabelledBy = _c === void 0 ? "" : _c,
    _d = _a.buttonStyles,
    buttonStyles = _d === void 0 ? {} : _d,
    _e = _a.className,
    className = _e === void 0 ? "" : _e,
    _f = _a.isIconButton,
    isIconButton = _f === void 0 ? false : _f,
    _g = _a.isResponsive,
    isResponsive = _g === void 0 ? false : _g,
    _h = _a.imageStyles,
    imageStyles = _h === void 0 ? {} : _h,
    _j = _a.leadingText,
    leadingText = _j === void 0 ? "Current Choice" : _j,
    _k = _a.onChange,
    _onChange = _k === void 0 ? function () {} : _k,
    _l = _a.options,
    options = _l === void 0 ? {} : _l,
    _m = _a.selectedValue,
    selectedValue = _m === void 0 ? "" : _m,
    _o = _a.role,
    role = _o === void 0 ? LTSegmentedControl.Role.TABLIST : _o;
  var buttonRefs = useRef(options.map(function () {
    return /*#__PURE__*/React.createRef();
  }));
  var handleKeyDown = function handleKeyDown(e, index, option) {
    var _a, _b, _c, _d;
    var newIndex = index;
    if (isEnterOrSpacePressed(e)) {
      _onChange(index, option);
    } else if ((e === null || e === void 0 ? void 0 : e.key) === "ArrowRight") {
      newIndex = (index + 1) % options.length;
      (_b = (_a = buttonRefs.current[newIndex]) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.focus();
    } else if (e.key === "ArrowLeft") {
      newIndex = (index - 1 + options.length) % options.length;
      (_d = (_c = buttonRefs.current[newIndex]) === null || _c === void 0 ? void 0 : _c.current) === null || _d === void 0 ? void 0 : _d.focus();
    }
  };
  var ltSelectOptions = options.map(function (option, index) {
    return {
      index: index,
      name: "".concat(option.label),
      value: option.value,
      isSelected: option.value === selectedValue
    };
  });
  var getSelectedIndexAndOption = function getSelectedIndexAndOption(selection) {
    var selectedIndex = ltSelectOptions === null || ltSelectOptions === void 0 ? void 0 : ltSelectOptions.findIndex(function (option) {
      return (option === null || option === void 0 ? void 0 : option.value) === (selection === null || selection === void 0 ? void 0 : selection.value);
    });
    return [selectedIndex, ltSelectOptions[selectedIndex]];
  };
  var itemRole = role === LTSegmentedControl.Role.TABLIST ? "tab" : "radio";
  return /*#__PURE__*/React.createElement(React.Fragment, null, isResponsive && /*#__PURE__*/React.createElement(SelectContainer, null, /*#__PURE__*/React.createElement(LTSelect, {
    leadingText: leadingText,
    options: ltSelectOptions,
    onChange: function onChange(selectedObjects) {
      _onChange.apply(void 0, getSelectedIndexAndOption(selectedObjects[0]));
    },
    selectedFilters: [selectedValue]
  })), /*#__PURE__*/React.createElement(SegmentedControlInner, {
    "aria-orientation": "horizontal",
    className: className,
    role: role,
    isResponsive: isResponsive,
    "aria-label": ariaLabel || undefined,
    "aria-labelledby": ariaLabelledBy || undefined
  }, options.map(function (option, index) {
    var _a = option.contentId,
      contentId = _a === void 0 ? undefined : _a,
      _b = option.icon,
      icon = _b === void 0 ? null : _b,
      _c = option.id,
      id = _c === void 0 ? "" : _c,
      _d = option.label,
      label = _d === void 0 ? "" : _d,
      _e = option.value,
      value = _e === void 0 ? "" : _e;
    var segmentedControlButtonClass = "lt-lib-segmented-control__label lt-lib-segmented-control__label--start ".concat(value === selectedValue ? "selectedSegmentedValue" : "segmentedValue");
    return /*#__PURE__*/React.createElement(SegmentedControlButton, __assign({}, role === LTSegmentedControl.Role.TABLIST && {
      "aria-controls": value === selectedValue ? contentId : undefined
    }, {
      "aria-label": isIconButton && label ? label : undefined,
      "aria-labelledby": !label && value ? "".concat(value, "-label-").concat(contentId) : undefined
    }, role === LTSegmentedControl.Role.TABLIST && {
      "aria-selected": value === selectedValue
    }, role === LTSegmentedControl.Role.RADIOGROUP && {
      "aria-checked": value === selectedValue
    }, {
      className: "segmented_control_button",
      id: id,
      key: value,
      onClick: function onClick() {
        return _onChange(index, option);
      },
      onKeyDown: function onKeyDown(e) {
        return handleKeyDown(e, index, option);
      },
      ref: buttonRefs.current[index],
      role: itemRole,
      tabIndex: value === selectedValue ? 0 : -1
    }), /*#__PURE__*/React.createElement("span", {
      className: segmentedControlButtonClass,
      id: "".concat(value, "-label-").concat(contentId),
      style: buttonStyles
    }, icon && /*#__PURE__*/React.createElement("img", {
      alt: "",
      "aria-hidden": "true",
      src: icon,
      style: __assign({
        display: "inline",
        marginRight: isIconButton ? "" : "4px",
        marginBottom: "1px"
      }, imageStyles)
    }), isIconButton ? "" : label));
  })));
};
var SelectContainer = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: none;\n  @media only screen and (max-width: 600px) {\n    display: inline-block;\n    margin: 10px;\n  }\n"], ["\n  display: none;\n  @media only screen and (max-width: 600px) {\n    display: inline-block;\n    margin: 10px;\n  }\n"])));
var SegmentedControlInner = styled.div(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  border: 1px solid ", ";\n  border-radius: 6px;\n  display: flex;\n  width: fit-content;\n\n  .segmented_control_button:first-child {\n    border-top-left-radius: 6px;\n    border-bottom-left-radius: 6px;\n  }\n\n  .segmented_control_button:last-child {\n    border-top-right-radius: 6px;\n    border-bottom-right-radius: 6px;\n  }\n  ", "\n"], ["\n  border: 1px solid ", ";\n  border-radius: 6px;\n  display: flex;\n  width: fit-content;\n\n  .segmented_control_button:first-child {\n    border-top-left-radius: 6px;\n    border-bottom-left-radius: 6px;\n  }\n\n  .segmented_control_button:last-child {\n    border-top-right-radius: 6px;\n    border-bottom-right-radius: 6px;\n  }\n  ", "\n"])), themeGet("colors.border.default"), function (_a) {
  var isResponsive = _a.isResponsive;
  return isResponsive && "\n    @media only screen and (max-width: 600px) {\n      display: none;\n    }\n  ";
});
var SegmentedControlButton = styled.span(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  background-color: #f7f7f7;\n  &:focus-visible {\n    outline: 2px solid #101010 !important;\n    outline-offset: -2px;\n  }\n"], ["\n  background-color: #f7f7f7;\n  &:focus-visible {\n    outline: 2px solid #101010 !important;\n    outline-offset: -2px;\n  }\n"])));
LTSegmentedControl.Role = {
  TABLIST: "tablist",
  RADIOGROUP: "radiogroup"
};
var templateObject_1, templateObject_2, templateObject_3;

export { LTSegmentedControl as default };
